import React from "react";
import { VideoPlayerGroup, PlaybackControls, DataTable } from "../molecules";
import { Button, Icon, Spinner } from "../atoms";

export function calculateDynamicMaxTime(values, fallback = 100) {
  const validValues = (values || []).map((v) =>
    v !== undefined && v !== null && !isNaN(v) ? Number(v) : 0
  );
  const max = Math.max(0, ...validValues);
  if (max <= 0) return fallback;

  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  const ratio = max / magnitude;

  const niceSteps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  const step = niceSteps.find((s) => s >= ratio - 1e-9) || 10;
  return Math.round(step * magnitude);
}

const GRID_ROWS = {
  horizontal: "lg:grid-rows-[minmax(0,58fr)_minmax(0,42fr)]",
  vertical: "lg:grid-rows-[minmax(0,1fr)]",
};

const TABLE_PLACEMENT = {
  horizontal: "lg:col-span-8 lg:col-start-1 lg:row-start-2",
  vertical: "lg:col-span-6 lg:col-start-1 lg:row-start-1 lg:pt-[42px]",
};

const METRICS_PLACEMENT = {
  horizontal: "flex lg:col-span-4 lg:col-start-9 lg:row-start-2",
  vertical: "hidden",
};

const ACTION_BUTTON = "!min-w-0 flex-1 !h-8 !px-3 !py-0 !text-xs";

export function MetricsTable({
  metrics,
  maxTimeScale: propMaxTimeScale,
  translate = (key) => key,
}) {
  const data = metrics || {};
  const num = (value) =>
    value === undefined || value === null || isNaN(value) ? 0 : Number(value);

  const rows = [
    {
      key: "visuMotor",
      label: "VC [ms]",
      tooltip: translate("velocidad_cognitiva"),
      total: data.totalVisuMotor,
      average: data.averageVisuMotor,
      deviation: data.standardDeviationVisuMotor,
    },
    {
      key: "motor",
      label: "VM [ms]",
      tooltip: translate("velocidad_motriz"),
      total: data.totalMotor,
      average: data.averageMotor,
      deviation: data.standardDeviationMotor,
    },
    {
      key: "cognitiveMotor",
      label: "VCM [ms]",
      tooltip: translate("velocidad_cmotriz"),
      total: data.totalCognitiveMotor,
      average: data.averageCognitiveMotor,
      deviation: data.standardDeviationCognitiveMotor,
    },
  ];

  const candidateUpperBounds = rows.map(
    (row) => num(row.average) + num(row.deviation)
  );
  if (data.maxPlayDuration) candidateUpperBounds.push(num(data.maxPlayDuration));
  if (data.maxDuration) candidateUpperBounds.push(num(data.maxDuration));

  const maxTimeScale =
    propMaxTimeScale ||
    data.maxTimeScale ||
    calculateDynamicMaxTime(candidateUpperBounds, 100);

  const correctPct = num(data.correctPercentage);
  const errorPct = num(data.errorPercentage);

  return (
    <div className="box-border flex w-full flex-col rounded-md bg-base p-2.5 shadow-sm">
      <div className="flex h-6 w-full overflow-hidden rounded-sm shadow-sm tabular-nums">
        <div
          className="flex items-center justify-center gap-1 bg-success-faded px-1 text-xs font-semibold text-success-fg"
          style={{ flexGrow: correctPct || 0.0001, flexBasis: 0 }}
        >
          <span>{translate("acierto")}:</span>
          <span className="tabular-nums">{correctPct}%</span>
        </div>
        <div
          className="flex items-center justify-center gap-1 bg-danger-faded px-1 text-xs font-semibold text-danger-fg"
          style={{ flexGrow: errorPct || 0.0001, flexBasis: 0 }}
        >
          <span>{translate("error")}:</span>
          <span className="tabular-nums">{errorPct}%</span>
        </div>
      </div>

      <div className="mt-4 flex flex-col">
        <div className="mb-1.5 flex w-full items-center gap-2 px-1">
          <div className="w-[72px] shrink-0" />
          <div className="relative h-4 flex-1 text-xs font-semibold text-secondary tabular-nums">
            <span className="absolute left-0 top-0 leading-none">0 ms</span>
            <span className="absolute left-1/2 top-0 -translate-x-1/2 leading-none">
              {Math.round(maxTimeScale / 2)} ms
            </span>
            <span className="absolute right-0 top-0 leading-none">
              {maxTimeScale} ms
            </span>
          </div>
          <div className="w-[90px] shrink-0" />
        </div>

        <div className="flex flex-col gap-3.5">
          {rows.map((row) => {
            const mean = num(row.average);
            const stdDev = num(row.deviation);
            const calculatedTotal = num(row.total);
            const fillPercentage = Math.min((mean / maxTimeScale) * 100, 100);
            const leftBound = Math.max(0, ((mean - stdDev) / maxTimeScale) * 100);
            const rightBound = Math.min(100, ((mean + stdDev) / maxTimeScale) * 100);
            const whiskerWidth = Math.max(0, rightBound - leftBound);

            return (
              <div key={row.key} className="flex w-full flex-col gap-1 px-1">
                <div className="flex h-6 w-full items-center gap-2">
                  <span
                    className="w-[72px] shrink-0 truncate text-sm font-semibold text-primary"
                    data-tooltip={row.tooltip}
                  >
                    {row.label}
                  </span>
                  <div className="relative h-5 flex-1 overflow-hidden rounded-sm bg-surface shadow-sm">
                    <div
                      className="h-full bg-info-faded transition-all duration-200"
                      style={{ width: `${fillPercentage}%` }}
                    />
                    {fillPercentage <= 80 ? (
                      <div
                        className="absolute inset-y-0 flex items-center pl-1.5 text-xs font-semibold text-info-fg tabular-nums pointer-events-none whitespace-nowrap"
                        style={{ left: `${fillPercentage}%` }}
                      >
                        <span>{mean}</span>
                        <span className="ml-0.5">ms</span>
                      </div>
                    ) : (
                      <div
                        className="absolute inset-y-0 left-0 flex items-center justify-end pr-1.5 text-xs font-semibold text-info-fg tabular-nums pointer-events-none whitespace-nowrap"
                        style={{ width: `${fillPercentage}%` }}
                      >
                        <span>{mean}</span>
                        <span className="ml-0.5">ms</span>
                      </div>
                    )}
                  </div>
                  <span className="flex w-[90px] shrink-0 items-center justify-end gap-1 text-sm font-medium text-primary tabular-nums">
                    <span className="text-xs text-secondary">Tot:</span>
                    <span>{calculatedTotal}</span>
                    <span>ms</span>
                  </span>
                </div>

                <div className="flex h-7 w-full items-start gap-2">
                  <div className="w-[72px] shrink-0" />
                  <div className="relative flex-1">
                    <div
                      className="absolute top-0 flex flex-col items-center"
                      style={{
                        left: `${leftBound}%`,
                        width: `${whiskerWidth}%`,
                      }}
                      data-testid={`whisker-${row.key}`}
                    >
                      <div className="relative flex h-2 w-full items-center">
                        <div className="absolute left-0 top-0 bottom-0 w-[1.5px] rounded-full bg-secondary" />
                        <div className="h-[1.5px] w-full bg-secondary" />
                        <div className="absolute right-0 top-0 bottom-0 w-[1.5px] rounded-full bg-secondary" />
                      </div>
                      <span className="mt-0.5 whitespace-nowrap text-xs font-semibold leading-tight text-secondary tabular-nums">
                        ±{Math.round(stdDev)} ms
                      </span>
                    </div>
                  </div>
                  <div className="w-[90px] shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AnalysisWorkspace({
  video = {},
  videoRefs,
  onSeek,
  onProgress,
  onDuration,
  onEnded,
  onReady,
  playback,
  tabLabels,
  table = {},
  onRowSelect,
  onCellClick,
  onCheckboxChange,
  currentPlay = {},
  actions = {},
  metrics,
  maxTimeScale,
  frameLabel,
  timeMsLabel,
  viewMode = "horizontal",
  translate = (key) => key,
  className = "",
}) {
  const controls =
    playback &&
    (playback.onSkipBackDouble ||
      playback.onFrameBack ||
      playback.onPlayPauseToggle ||
      playback.onFrameForward ||
      playback.onSkipForwardDouble) ? (
      <PlaybackControls
        isPlaying={playback.isPlaying}
        onSkipBackDouble={playback.onSkipBackDouble}
        onFrameBack={playback.onFrameBack}
        onPlayPauseToggle={playback.onPlayPauseToggle}
        onFrameForward={playback.onFrameForward}
        onSkipForwardDouble={playback.onSkipForwardDouble}
        disabled={playback.disabled}
        skipBackTooltip={translate("retroceder_estimulo")}
        frameBackTooltip="Retroceder Frame"
        playPauseTooltip={playback.isPlaying ? "Pausar" : "Reproducir"}
        frameForwardTooltip="Avanzar Frame"
        skipForwardTooltip={translate("avanzar_estimulo")}
      />
    ) : undefined;

  const processing = actions.processing || {};
  const processingContent =
    processing.value === 0 ? (
      <Spinner size="sm" />
    ) : processing.value === 2 ? (
      <Icon name="x-mark" />
    ) : (
      <Icon name="auto-fix" />
    );
  const processingMessage =
    processing.value === 0
      ? translate("procesando")
      : processing.value === 2
        ? translate("error_procesando")
        : translate("procesar");

  const markActions = (
    <>
      <Button
        variant="accent"
        className={ACTION_BUTTON}
        id="AddDecisionMakingMark"
        icon="foot"
        disabled={actions.marksDisabled}
        onClick={actions.onDecisionMaking}
        title={translate("anadir_marca_decision")}
      >
        DM
      </Button>
      <Button
        variant="accent"
        className={ACTION_BUTTON}
        id="AddArrivalMark"
        icon="target"
        disabled={actions.marksDisabled}
        onClick={actions.onArrival}
        title={translate("anadir_marca_arrival")}
      >
        Arrival
      </Button>
    </>
  );

  const saveLabel =
    actions.saveStatus === "saving"
      ? "Guardando..."
      : actions.saveStatus === "saved"
        ? "Guardado"
        : actions.saveStatus === "error"
          ? "Error"
          : translate("guardar");

  const sessionActions = (
    <>
      <Button
        variant="accent-faded"
        className={ACTION_BUTTON}
        id="AutoAnalysis"
        disabled={actions.autoAnalysisDisabled}
        onClick={actions.onAutoAnalysis}
        title={translate("procesar_pasos")}
      >
        <span className="inline-flex items-center gap-1.5">
          {processingContent}
          <span>{processingMessage}</span>
        </span>
      </Button>
      <Button
        variant="control"
        className={ACTION_BUTTON}
        id="SaveAnalizeSession"
        icon="save"
        onClick={actions.onSave}
        title={translate("guardar_info_sesion")}
      >
        {saveLabel}
      </Button>
      <Button
        variant="danger-faded"
        className={ACTION_BUTTON}
        icon="erase"
        onClick={actions.onClear}
        title={translate("borrar_analisis")}
      >
        {translate("borrar")}
      </Button>
    </>
  );

  return (
    <main
      role="main"
      className={`box-border flex h-full min-h-0 w-full flex-1 flex-col gap-6 overflow-hidden px-8 py-4 ${className}`}
    >
      <div
        className={`grid min-h-0 w-full flex-1 grid-cols-12 gap-6 overflow-hidden ${GRID_ROWS[viewMode]}`}
      >
        <VideoPlayerGroup
          viewMode={viewMode}
          videoRefs={videoRefs}
          url={video.url}
          playing={video.playing}
          loading={video.loading}
          currentFrame={video.currentFrame}
          fps={video.fps}
          videoDuration={video.videoDuration}
          currentTime={video.currentTime}
          onSeek={onSeek}
          onProgress={onProgress}
          onDuration={onDuration}
          onEnded={onEnded}
          onReady={onReady}
          controls={controls}
          tabLabels={tabLabels}
          frameLabel={frameLabel}
          timeMsLabel={timeMsLabel}
          currentPlay={{
            prevImage: currentPlay.prevImage,
            currentImage: currentPlay.currentImage,
            prevTimeMs: currentPlay.prevTimeMs,
            currentTimeMs: currentPlay.currentTimeMs,
            prevPlayNumber: currentPlay.prevPlayNumber,
            currentPlayNumber: currentPlay.currentPlayNumber,
            prevTitle: translate("jugada_anterior"),
            currentTitle: translate("jugada_actual"),
            playLabel: translate("jugada_"),
            timeMsLabel: timeMsLabel || translate("tiempo_ms_"),
          }}
          markActions={markActions}
          sessionActions={sessionActions}
        />
        <div
          className={`col-span-12 flex min-h-0 flex-col overflow-hidden rounded-sm ${TABLE_PLACEMENT[viewMode]}`}
        >
          <DataTable
            rows={table.rows}
            selectedRowIndex={table.selectedRowIndex}
            onRowSelect={onRowSelect}
            onCellClick={onCellClick}
            onCheckboxChange={onCheckboxChange}
            translate={translate}
            cellClassName={viewMode === "vertical" ? "py-2.5" : "py-1"}
          />
        </div>
        <div
          className={`col-span-12 min-h-0 flex-col gap-4 overflow-y-auto ${METRICS_PLACEMENT[viewMode]}`}
        >
          <MetricsTable
            metrics={metrics}
            maxTimeScale={maxTimeScale}
            translate={translate}
          />
        </div>
      </div>
    </main>
  );
}

export default AnalysisWorkspace;
